
import React, { useRef, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import MarkdownRenderer from "@/components/MarkdownRenderer";

interface GettingStartedSectionProps {
  registerSectionRef: (id: string, element: HTMLElement | null) => void;
}

const overviewMarkdown = `
Discovery Hub helps businesses efficiently:

- **Find and Track Leads**: Discover potential clients through the Places search feature
- **Score and Qualify Leads**: Use the automated scoring system to identify high-value opportunities
- **Manage Company Data**: Create multiple companies and invite team members
- **Track Activities**: Log meetings, calls, emails and other interactions
- **Store Documents**: Keep all related files in one place
- **Communicate Effectively**: Use email templates and call scripts
`;

const navigationMarkdown = `
The main navigation in Discovery Hub includes:

- **Header Navigation**: Contains links to main sections and user options
- **Company Switcher**: Located in the header to easily switch between companies
- **Main Dashboard**: Analytics and KPIs to track your sales performance
- **Leads**: Manage all your leads with multiple view options (cards, list, table)
- **+ Add Leads**: Find businesses in your target locations
- **Tasks**: Manage and track your team's tasks and to-dos
- **Campaign Planner**: Create AI-powered multi-channel marketing campaigns
- **Mail Merges**: View history and results of bulk outreach runs
- **Companies**: Manage your company profiles and team members
- **Documentation**: This help section you're reading now
- **Profile**: Access to your user settings and subscription details
`;

const GettingStartedSection = ({ registerSectionRef }: GettingStartedSectionProps) => {
  // Create refs for each section
  const overviewRef = useRef<HTMLHeadingElement>(null);
  const navigationRef = useRef<HTMLHeadingElement>(null);
  const authenticationRef = useRef<HTMLHeadingElement>(null);
  const dashboardRef = useRef<HTMLHeadingElement>(null);
  
  // Register all section refs when component mounts or refs change
  useEffect(() => {
    registerSectionRef('overview', overviewRef.current);
    registerSectionRef('navigation', navigationRef.current);
    registerSectionRef('authentication', authenticationRef.current);
    registerSectionRef('dashboard', dashboardRef.current);
  }, [registerSectionRef]);

  return (
    <>
      <h2 
        id="overview" 
        ref={overviewRef}
        className="text-2xl font-bold tracking-tight scroll-mt-16"
      >
        Application Overview
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>What is Discovery Hub?</CardTitle>
          <CardDescription>A comprehensive lead management and company tracking platform</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Discovery Hub is a powerful B2B lead management platform designed to help businesses efficiently manage leads, track companies, 
            and optimize their sales process. It provides a comprehensive suite of tools for lead scoring, activity tracking, company management, 
            and user collaboration.
          </p>
          
          <MarkdownRenderer content={overviewMarkdown} />
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Subscription Plans</h4>
            <p>
              Discovery Hub offers different subscription plans to fit your business needs. Each plan comes with different lead import limits and features.
              You can manage your subscription from the Profile section.
            </p>
          </div>
        </CardContent>
      </Card>

      <h2 
        id="navigation" 
        ref={navigationRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Navigation
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Navigating the Platform</CardTitle>
          <CardDescription>Understanding the interface and key sections</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            The main navigation of Discovery Hub is designed to be intuitive and user-friendly, with responsive design
            for both desktop and mobile devices.
          </p>
          
          <MarkdownRenderer content={navigationMarkdown} />
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Company Switcher</h4>
            <p>
              The Company Switcher in the header allows you to quickly switch between different companies you have access to. 
              All data you view and actions you take will be in the context of the currently selected company.
            </p>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Mobile Navigation</h4>
            <p>
              On mobile devices, the main navigation collapses into a menu accessible via the menu button in the top-right corner.
              All features remain accessible on mobile with adapted layouts.
            </p>
          </div>
        </CardContent>
      </Card>
      
      <h2 
        id="authentication" 
        ref={authenticationRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Authentication
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Authentication</CardTitle>
          <CardDescription>User management and access control</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Discovery Hub provides secure authentication to protect your data. Users must log in with their credentials to access the platform.
          </p>
          
          <div className="space-y-2">
            <h4 className="font-semibold">Account Creation</h4>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Navigate to the login page</li>
              <li>Click on "Sign up" to create a new account</li>
              <li>Enter your email address and create a password</li>
              <li>Verify your email address via the link sent to your inbox</li>
              <li>Complete your profile with your name and other details</li>
            </ol>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">User Roles</h4>
            <p>
              Discovery Hub supports different user roles with varying levels of access:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Admin:</strong> Full access to all features including user management</li>
              <li><strong>Member:</strong> Standard access to leads and company data</li>
              <li><strong>Viewer:</strong> Read-only access to company data</li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Company Setup</h4>
            <p>
              After creating an account, you can:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Create a new company</li>
              <li>Accept an invitation to join an existing company</li>
              <li>Request access to an existing company</li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Password Reset</h4>
            <p>
              If you've forgotten your password, click the "Forgot password?" link on the login page and follow the instructions
              sent to your email address.
            </p>
          </div>
        </CardContent>
      </Card>
      
      <h2 
        id="dashboard" 
        ref={dashboardRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Dashboard
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Dashboard Overview</CardTitle>
          <CardDescription>Analytics and key performance indicators</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            The dashboard provides a visual summary of your leads, activities, and performance metrics. It's designed to give you 
            a quick overview of your sales pipeline and lead management effectiveness.
          </p>
          
          <div className="space-y-2">
            <h4 className="font-semibold">Key Metrics Cards</h4>
            <p>
              The top section displays important numbers at a glance:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Total Leads:</strong> Count of all leads in your company</li>
              <li><strong>New Leads:</strong> Recently added leads with "New" status</li>
              <li><strong>Favorite Leads:</strong> Leads marked as favorites</li>
              <li><strong>Average Rating:</strong> The mean rating across all your leads</li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Activity Metrics</h4>
            <p>
              Track engagement with your leads:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Recent Activities:</strong> Count of activities in the last 7 days</li>
              <li><strong>Pending Tasks:</strong> Number of tasks awaiting completion</li>
              <li><strong>Completed Tasks:</strong> Number of finished tasks</li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Data Visualizations</h4>
            <p>
              The dashboard includes interactive charts:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Lead Status Distribution:</strong> Pie chart showing breakdown by status</li>
              <li><strong>Top Lead Categories:</strong> Bar chart showing most common business categories</li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Recent Leads Table</h4>
            <p>
              A summary table showing your most recently added leads with their name, category, status, and date added.
              This gives you quick access to your newest prospects.
            </p>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Data Refresh</h4>
            <p>
              Dashboard metrics can be manually refreshed using the Refresh button in the top-right corner
              of the dashboard.
            </p>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default GettingStartedSection;
